import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/api';

/**
 * Custom hook to fetch category-specific snapshot data
 * @param {string|string[]} category - Category name or array of category names to sum: 'stocks', 'crypto', 'preciousMetals', 'cash', 'realEstate', 'liabilities'
 * @param {number} days - Number of days to fetch (default: 90)
 * @returns {Object} { data, loading, error }
 */
export const useCategorySnapshots = (category, days = 90) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Convert category to stable string for dependency tracking
  // This prevents infinite loop when category is an array
  const categoryKey = Array.isArray(category) ? category.join(',') : category;

  useEffect(() => {
    const fetchCategorySnapshots = async () => {
      try {
        setLoading(true);
        setError(null);

        // Calculate start date
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);

        // Fetch snapshots
        const response = await axios.get(`${API_BASE_URL}/snapshots`, {
          params: {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
          },
          withCredentials: true
        });

        if (response.data.success && response.data.snapshots) {
          // Support both single category and array of categories
          const categories = Array.isArray(category) ? category : [category];

          // Extract category-specific data from snapshots
          const categoryData = response.data.snapshots
            .map(snapshot => {
              // Sum values from all specified categories
              const categoryValue = categories.reduce((sum, cat) => {
                return sum + (snapshot.categories?.[cat]?.value || 0);
              }, 0);

              return {
                date: snapshot.date,
                value: categoryValue
              };
            })
            .sort((a, b) => a.date.localeCompare(b.date)); // Sort by date ascending

          setData(categoryData);
        } else {
          setData([]);
        }
      } catch (err) {
        console.error(`Error fetching ${categoryKey} snapshots:`, err);
        setError(err.response?.data?.error || err.message || 'Failed to fetch snapshot data');
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategorySnapshots();
    // `category` intentionally omitted — see the `categoryKey` comment above;
    // callers may pass a new array literal each render, so depending on
    // `category` directly would refetch every render (infinite loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryKey, days]); // Use categoryKey instead of category to prevent infinite loop

  return { data, loading, error };
};
